import {
  ChangeDetectionStrategy,
  Component,
  ViewEncapsulation,
  computed,
  inject,
  input,
} from '@angular/core';

import { paletteFor } from '../charts/chart-palette';
import { ComparisonChartComponent } from '../charts/comparison-chart.component';
import { ReportChromeService } from '../report-chrome.service';
import { ReportTPipe } from '../report-i18n.pipe';
import { ReportI18nService } from '../report-i18n.service';
import { ReportRevealDirective } from '../report-reveal.directive';
import { CrossSubjectView } from '../report.model';

/** Une entrée de légende : sa catégorie, sa couleur, sa clé de libellé. */
interface LegendEntry {
  cat: number;
  color: string;
  key: string;
}

/**
 * Bloc « comparaison des performances » — le second des deux blocs d'ensemble.
 *
 * **Deux séries, pas trois.** La maquette en portait une troisième, « moyenne
 * de classe » (`data-cat="1"`), retirée par décision produit : un résultat de
 * diagnostic n'appartient qu'à l'élève et ne se compare pas à ses camarades.
 * Le seul repère extérieur est l'objectif, qui est une barre fixe. Les
 * catégories restantes gardent leurs numéros d'origine — `0` pour l'élève, `2`
 * pour l'objectif — parce qu'ils désignent des rangs dans la palette
 * catégorielle, et non des positions dans la légende : les renuméroter
 * changerait les couleurs.
 *
 * Les pastilles sont liées à la palette du graphique plutôt que recopiées :
 * légende et barres ne peuvent alors pas diverger, y compris à la bascule de
 * thème, où les trois couleurs catégorielles changent toutes.
 */
@Component({
  selector: 's2e-report-comparison',
  standalone: true,
  encapsulation: ViewEncapsulation.None,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [ReportTPipe, ReportRevealDirective, ComparisonChartComponent],
  templateUrl: './comparison.section.html',
  host: { '[style.display]': "'contents'" },
})
export class ComparisonSection {
  private readonly i18n = inject(ReportI18nService);
  protected readonly chrome = inject(ReportChromeService);

  readonly cross = input.required<CrossSubjectView>();

  protected readonly legend = computed<LegendEntry[]>(() => {
    const palette = paletteFor(this.chrome.theme());
    return [
      // La légende suit le graphique : l'objectif porte l'orangé du trio, pour
      // qu'il se distingue du bleu de l'élève sans dépendre de la teinte seule.
      { cat: 0, color: palette.categorical[0], key: 'comparison.student' },
      { cat: 1, color: palette.categorical[1], key: 'comparison.target' },
    ];
  });

  protected readonly seriesLabels = computed(() => ({
    student: this.i18n.t('comparison.student'),
    target: this.i18n.t('comparison.target'),
  }));

  protected readonly ariaLabel = computed(() =>
    this.i18n.t('chart.ariaBars', { count: this.cross().comparison.length }),
  );

  /**
   * Mise en forme des pourcentages du graphique.
   *
   * Le graphique ne connaît pas la locale ; sans cette fonction ses
   * graduations s'écriraient « 25% » là où le reste du rapport écrit
   * « 25 % » — et en arabe, avec les marques bidi qui vont avec.
   */
  protected readonly percentFormat = computed<(value: number) => string>(() => {
    /* Lecture volontaire de la langue : c'est le changement d'identité de la
       fonction qui fait recalculer les graduations. Sans cette dépendance, le
       graphique garderait celle de la langue précédente. */
    this.i18n.lang();
    return (value: number) => this.i18n.pct(value);
  });
}
